import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import axios from 'axios'
import { API_BASE_URL } from '../../constants/system'

export interface DashboardEditHistoryEntry {
  id: string
  created_at: number
  dashboard_id: string
  customer: string
  user_email: string
  widget_title: string
  widget_section: string
  edit_type: string
  before_text: string
  after_text: string
}

interface DashboardHistoryPanelProps {
  active: boolean
  customer: string
  dashboardId: string
}

const formatTime = (epochSeconds: number) => {
  const ms = epochSeconds > 1e12 ? epochSeconds : epochSeconds * 1000
  return new Date(ms).toLocaleString()
}

/** The fetch-and-render body of the dashboard edit history — the "Text edits"
 *  sub-tab of both History surfaces, via DashboardHistoryTabs (#974). `active`
 *  gates the fetch so it only runs while the consumer is actually visible. */
const DashboardHistoryPanel: React.FC<DashboardHistoryPanelProps> = ({
  active,
  customer,
  dashboardId,
}) => {
  const [entries, setEntries] = useState<DashboardEditHistoryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active || !customer || !dashboardId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    axios
      .get<{ entries?: DashboardEditHistoryEntry[] } | DashboardEditHistoryEntry[]>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/edit-history`
      )
      .then(res => {
        if (cancelled) return
        const data = res.data
        const list = Array.isArray(data) ? data : (data.entries || [])
        // Newest first
        list.sort((a, b) => (b.created_at || 0) - (a.created_at || 0))
        setEntries(list)
      })
      .catch(err => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load history')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [active, customer, dashboardId])

  return (
    <Box>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && entries.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          No edits recorded yet.
        </Typography>
      )}
      {!loading && !error && entries.map(entry => (
        <Accordion key={entry.id} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entry.widget_title || entry.widget_section || '(untitled)'}
                </Typography>
                <Chip label={entry.edit_type || 'edit'} size="small" />
                {entry.after_text === '' && entry.before_text !== '' && (
                  <Chip label="overwritten by regenerate" size="small" color="warning" />
                )}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {formatTime(entry.created_at)}
                {entry.user_email ? ` · ${entry.user_email}` : ''}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Before
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    backgroundColor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    fontSize: '13px',
                    maxHeight: 240,
                    overflow: 'auto',
                  }}
                >
                  {entry.before_text || <em style={{ color: '#999' }}>(empty)</em>}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  After
                </Typography>
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    backgroundColor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 1,
                    whiteSpace: 'pre-wrap',
                    fontSize: '13px',
                    maxHeight: 240,
                    overflow: 'auto',
                  }}
                >
                  {entry.after_text || <em style={{ color: '#999' }}>(empty)</em>}
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

export default DashboardHistoryPanel
